import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

// Error formatter
function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not auth, object = auth
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
            setUser(data);
        } catch (e) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const { data } = await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true }
            );
            setUser(data);
            return { success: true, user: data };
        } catch (e) {
            const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            return { success: false, error };
        }
    };

    const register = async (name, email, password, broker, extra = {}) => {
        try {
            const { data } = await axios.post(
                `${API}/auth/register`,
                { name, email, password, broker, ...extra },
                { withCredentials: true }
            );
            setUser(data);
            return { success: true, user: data };
        } catch (e) {
            const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
            return { success: false, error };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        } catch (e) {
            console.error('Logout error:', e);
        } finally {
            setUser(false);
        }
    };

    const refreshAuth = async () => {
        await checkAuth();
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        refreshAuth,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isStudent: user?.role === 'student'
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export default AuthContext;
